import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { SupportProvider } from '../../../contexts/SupportContext';
import cloudApi from '../../../services/cloudApi';
import AutomationsView from '../AutomationsView';
import DatabaseView from '../DatabaseView';
import AccountView from '../AccountView';
import PlansView from '../PlansView';
import PlansViewV2 from '../PlansViewV2/PlansViewV2';
import TopUpsView from '../TopUpsView/TopUpsView';
import IntegrationsView from '../IntegrationsView';
import McpView from '../McpView';
import AdminView from '../AdminView';
import SupportModal from '../SupportModal';
import HardPaywall from '../../../components/HardPaywall';
import AnnouncementBanner from '../../../announcements/AnnouncementBanner';
import { useAnnouncements } from '../../../announcements/useAnnouncements';
import { getPlanLabel } from '../../../utils/planDisplay';
import { ONBOARDING_ENABLED } from '../../Onboarding/Onboarding';
import {
  HiOutlineUser,
  HiOutlineCreditCard,
  HiOutlinePlusCircle,
  HiOutlinePuzzlePiece,
  HiOutlineCog6Tooth,
  HiOutlineArrowRightOnRectangle,
  HiOutlineBars3,
  HiOutlineXMark,
  HiOutlineChevronDown,
  HiOutlineCheck,
  HiOutlineCircleStack,
  HiOutlineSun,
  HiOutlineMoon,
  HiOutlineLifebuoy,
} from 'react-icons/hi2';
import { IoIosRocket } from 'react-icons/io';
import { TutorialsDropdown, TutorialsMobileList } from './TutorialsDropdown';

const formatCredits = (num) => {
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  return num.toLocaleString();
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const DashboardView = () => {
  const { isAuthenticated, user, logout, loading, workspaces, currentWorkspace, subscription, credits, switchWorkspace, isAdmin, viewAsUser, enterViewAs, exitViewAs } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const { view } = useParams();
  const location = useLocation();
  const activeView =
    location.pathname === '/plans' ? 'plans'
    : location.pathname === '/topups' ? 'topups'
    : location.pathname === '/plans-legacy' ? 'plans-legacy'
    : (view || 'automations');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const plan = subscription?.subscription?.plan?.name;
  const isFreePlan = !plan || plan === 'free';
  const planName = (plan && plan !== 'free') ? plan : null;
  const planLabel = getPlanLabel(subscription);

  // Status announcements (service notices, product messages). Add/remove
  // messages in src/announcements/announcements.js — no changes needed here.
  const { announcements, dismiss: dismissAnnouncement } = useAnnouncements({
    enabled: isAuthenticated && !!currentWorkspace,
    plan: plan || 'free',
    view: activeView,
  });

  // Hard paywall: cloud access requires a paid plan. Free users keep access to
  // plans/account/admin so they can upgrade or manage; everything else is gated.
  // Gate on the subscription object being present (not just !loading): a null
  // subscription must never be read as "free", or paid users flash the paywall
  // while billing is still resolving.
  // Suppressed only while the onboarding modal is up, so the paywall doesn't
  // sit behind it. With onboarding off, a pending flag must not gate the paywall.
  const onboardingPending = ONBOARDING_ENABLED && user?.onboardingCompleted === false;
  const ALLOWED_FREE_VIEWS = ['plans', 'account', 'admin'];
  const isPaywalled =
    subscription !== null
    && isFreePlan
    && !viewAsUser
    && !onboardingPending
    && !ALLOWED_FREE_VIEWS.includes(activeView);

  // Other workspaces the user belongs to that are paid — offered as a switch on
  // the paywall (paid plans carry the chrome_extension_pro feature flag).
  const paidWorkspaces = (workspaces || []).filter(
    (w) => w.id !== currentWorkspace?.id && w.features?.chrome_extension_pro
  );
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] = useState(0);
  const [supportInitialTicketId, setSupportInitialTicketId] = useState(null);
  const [supportPrefill, setSupportPrefill] = useState(null);
  // Lets views deep in the tree open the support modal (see SupportContext).
  // A prefill seeds the new-request form, so the modal opens on that tab.
  const supportApi = useMemo(() => ({
    openSupport: ({ prefill = null } = {}) => {
      setSupportInitialTicketId(null);
      setSupportPrefill(prefill);
      setUserMenuOpen(false);
      setSupportModalOpen(true);
    },
  }), []);
  const [tutorialsPanelOpen, setTutorialsPanelOpen] = useState(false);
  const tutorialsTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Sort workspaces: default first, then collaborative workspaces, then solo-owner ones, alpha within each group
  const sortedWorkspaces = useMemo(() => {
    if (!workspaces || workspaces.length === 0) return [];
    const defaultId = user?.default_workspace_id;
    const isPersonal = (w) => w.role === 'owner' && Number(w.member_count) <= 1;
    return [...workspaces].sort((a, b) => {
      if (a.id === defaultId && b.id !== defaultId) return -1;
      if (b.id === defaultId && a.id !== defaultId) return 1;
      const aPersonal = isPersonal(a);
      const bPersonal = isPersonal(b);
      if (aPersonal !== bPersonal) return aPersonal ? 1 : -1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [workspaces, user?.default_workspace_id]);

  const openTutorialsPanel = () => {
    clearTimeout(tutorialsTimeoutRef.current);
    setTutorialsPanelOpen(true);
    setUserMenuOpen(false);
  };

  const closeTutorialsPanelDelayed = () => {
    tutorialsTimeoutRef.current = setTimeout(() => {
      setTutorialsPanelOpen(false);
    }, 150);
  };

  useEffect(() => {
    return () => clearTimeout(tutorialsTimeoutRef.current);
  }, []);

  // Handle ?viewAs= URL parameter for frontend-admin integration
  useEffect(() => {
    const viewAsId = searchParams.get('viewAs');
    if (!viewAsId || !isAuthenticated || !isAdmin || viewAsUser) return;
    const name = searchParams.get('viewAsName') || '';
    const email = searchParams.get('viewAsEmail') || '';
    enterViewAs({ id: viewAsId, name, email }).then(() => {
      setSearchParams({}, { replace: true });
    }).catch((err) => {
      console.error('Failed to enter view-as mode via URL:', err);
      setSearchParams({}, { replace: true });
    });
  }, [isAuthenticated, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle ?support=<ticketId> deep link
  useEffect(() => {
    const supportTicketId = searchParams.get('support');
    if (!supportTicketId || !isAuthenticated) return;
    searchParams.delete('support');
    setSearchParams(searchParams, { replace: true });
    setSupportInitialTicketId(supportTicketId);
    setSupportModalOpen(true);
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll for unread support replies — faster when modal is open
  useEffect(() => {
    if (!isAuthenticated || !currentWorkspace) return;
    const fetchUnread = async () => {
      try {
        const result = await cloudApi.getSupportUnreadCount();
        setSupportUnreadCount(result.unreadCount || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, supportModalOpen ? 15_000 : 60_000);
    return () => clearInterval(interval);
  }, [isAuthenticated, currentWorkspace, supportModalOpen]);

  // NOTE: 'mcp' is intentionally absent from the nav. The view is admin-only —
  // only admins with the direct /dashboard/mcp URL render McpView; non-admins are
  // redirected to Integrations (see renderView), where the Claude and MCP cards
  // cover connecting AI agents and managing tokens for everyone.
  const mainNavItems = [
    { id: 'automations', label: 'Automations', icon: IoIosRocket },
    { id: 'database', label: 'Database', icon: HiOutlineCircleStack },
    { id: 'integrations', label: 'Integrations', icon: HiOutlinePuzzlePiece },
    { id: 'support', label: 'Support', icon: HiOutlineLifebuoy, onClick: () => setSupportModalOpen(true), badge: supportUnreadCount },
  ];

  const allNavigationItems = [
    ...mainNavItems,
    { id: 'plans', label: 'Plans', icon: HiOutlineCreditCard },
    { id: 'account', label: 'Account', icon: HiOutlineUser },
    ...(isAdmin ? [{ id: 'admin', label: 'Admin', icon: HiOutlineCog6Tooth }] : []),
  ];

  // Set page title based on active view. 'mcp' has no nav entry and is admin-only
  // (regular users manage Claude from the Integrations card, which opens a modal
  // rather than routing here), so give it an explicit label instead of the default.
  useEffect(() => {
    const currentLabel =
      allNavigationItems.find(item => item.id === activeView)?.label
      || (activeView === 'mcp' ? 'Claude & AI' : 'Automations');
    document.title = `Dashboard | ${currentLabel}`;
  }, [activeView, isAdmin]);

  // Redirect to signin if not authenticated, carrying the page they wanted so
  // sign-in lands them there instead of the dashboard root. Links from the
  // marketing site and the extension depend on this — those visitors are usually
  // signed out, and /dashboard/integrations would otherwise be lost on the way.
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      const destination = `${location.pathname}${location.search}`;
      navigate(destination === '/dashboard' ? '/signin' : `/signin?returnTo=${encodeURIComponent(destination)}`, { replace: true });
    }
  }, [isAuthenticated, loading, navigate, location.pathname, location.search]);

  // Close menus on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setUserMenuOpen(false);
        setTutorialsPanelOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const handleWorkspaceSwitch = async (workspaceId) => {
    try {
      await switchWorkspace(workspaceId);
      setUserMenuOpen(false);
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch workspace:', error);
    }
  };

  const renderView = () => {
    if (isPaywalled) {
      return (
        <HardPaywall
          onUpgrade={() => navigate('/plans')}
          paidWorkspaces={paidWorkspaces}
          onSwitchWorkspace={handleWorkspaceSwitch}
        />
      );
    }
    switch (activeView) {
      case 'automations': return <AutomationsView />;
      case 'database': return <DatabaseView />;
      case 'account': return <AccountView />;
      case 'plans': return <PlansViewV2 />;
      case 'plans-legacy': return <PlansView />;
      case 'topups': return <TopUpsView />;
      case 'integrations': return <IntegrationsView />;
      // The McpView marketing page is admin-only; regular users connect Claude
      // and manage MCP tokens from Integrations. Non-admins hitting
      // /dashboard/mcp directly go there.
      case 'mcp': return isAdmin ? <McpView /> : <Navigate to="/dashboard/integrations" replace />;
      case 'admin': return isAdmin ? <AdminView /> : <AutomationsView />;
      default: return <AutomationsView />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <SupportProvider value={supportApi}>
    <div className={`h-screen bg-gray-50 dark:bg-gray-950 flex flex-col relative overflow-hidden${viewAsUser ? ' pt-9' : ''}`}>

      {/* View As Banner */}
      {viewAsUser && (
        <div className="fixed top-0 inset-x-0 z-[60] h-9 flex items-center justify-between px-4 bg-gradient-to-r from-orange-500/[0.12] via-orange-500/[0.06] to-transparent dark:from-orange-500/[0.15] dark:via-orange-500/[0.08] dark:to-transparent backdrop-blur-sm border-b border-orange-500/30">
          <div className="flex items-center gap-2.5">
            <span className="relative flex h-2 w-2 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
            </span>
            <span className="text-[11px] font-medium text-orange-600/80 dark:text-orange-300/80 tracking-wide">Viewing as</span>
            <span className="text-[11px] font-semibold text-gray-900 dark:text-white">{viewAsUser.name || viewAsUser.email}</span>
            {viewAsUser.name && (
              <span className="text-[11px] text-orange-600/50 dark:text-orange-300/50 hidden sm:inline">({viewAsUser.email})</span>
            )}
          </div>
          <button
            onClick={exitViewAs}
            className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-orange-500/10 dark:bg-orange-500/15 text-orange-600 dark:text-orange-300 border border-orange-500/30 hover:bg-orange-500/20 dark:hover:bg-orange-500/25 hover:text-orange-700 dark:hover:text-orange-200 transition-all"
          >
            Exit ×
          </button>
        </div>
      )}

      {/* Top Navbar */}
      <header className="relative z-50 flex-shrink-0 bg-white/80 dark:bg-gray-950/80 backdrop-blur-xl border-b border-gray-200 dark:border-white/[0.08] shadow-sm dark:shadow-[0_1px_0_0_rgba(255,255,255,0.02)]">
        <div className="h-14 lg:h-16 px-4 lg:px-5 flex items-center gap-2 sm:gap-4 max-w-[1500px] mx-auto w-full">

          {/* Left Zone: Logo */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            {/* Hamburger - mobile only */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden -ml-1 p-1.5 rounded-xl text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
            >
              {mobileMenuOpen
                ? <HiOutlineXMark className="w-5 h-5" />
                : <HiOutlineBars3 className="w-5 h-5" />
              }
            </button>

            {/* Logo */}
            <div className="relative group cursor-pointer" onClick={() => navigate('/dashboard/automations')}>
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl p-[1.5px] bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 shadow-md shadow-cyan-500/15 group-hover:shadow-cyan-500/30 group-hover:scale-105 transition-all duration-200">
                <div className="w-full h-full bg-white rounded-[10px] p-0.5 flex items-center justify-center">
                  <img src="/logo-128.png" alt="Logo" className="w-full h-full" />
                </div>
              </div>
            </div>

            {/* Brand text */}
            <div className="hidden md:flex flex-col items-start">
              <p className="text-[8px] uppercase tracking-[0.2em] font-semibold text-gray-400 dark:text-gray-500 leading-tight">
                Ultimate Web Scraper
              </p>
              <span className="text-sm font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent tracking-tight leading-none">
                Cloud Platform
              </span>
            </div>

            {/* Divider */}
            <div className="hidden lg:block h-5 w-px bg-gray-200 dark:bg-white/[0.06] ml-2" />
          </div>

          {/* Center Zone: Nav Pills (desktop) */}
          <nav className="hidden lg:flex items-center gap-1">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeView === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => item.onClick ? item.onClick() : navigate(`/dashboard/${item.id}`)}
                  title={item.label}
                  className={`relative flex items-center gap-2 px-2.5 xl:px-3.5 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-gray-100 dark:bg-white/[0.08] text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-white/[0.08] shadow-sm dark:shadow-[0_0_12px_rgba(34,211,238,0.08)]'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.04]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden xl:inline">{item.label}</span>
                  {item.badge > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-0.5 rounded-full bg-gradient-to-r from-blue-400 to-cyan-400" />
                  )}
                </button>
              );
            })}

            <TutorialsDropdown
              isOpen={tutorialsPanelOpen}
              onOpen={openTutorialsPanel}
              onCloseDelayed={closeTutorialsPanelDelayed}
            />
          </nav>

          {/* Right Zone: Credits + Theme + User */}
          <div className="flex items-center gap-2 lg:gap-3 ml-auto">

            {/* Plan Badge / Upgrade CTA (desktop) */}
            {isFreePlan ? (
              <button
                onClick={() => navigate('/plans')}
                className="hidden lg:inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 shadow-md shadow-cyan-500/25 hover:shadow-cyan-500/40 hover:scale-[1.03] transition-all duration-200 whitespace-nowrap flex-shrink-0"
              >
                <HiOutlineCreditCard className="w-3.5 h-3.5 text-white/90" />
                <span className="text-xs font-bold text-white tracking-wide uppercase">
                  Upgrade Plan
                </span>
              </button>
            ) : planName ? (
              <button
                onClick={() => navigate('/plans')}
                className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-blue-500/10 via-cyan-500/10 to-purple-500/10 border border-cyan-400/20 hover:border-cyan-400/30 transition-all duration-200"
              >
                <span className="text-xs font-bold bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  {planLabel}
                </span>
              </button>
            ) : null}

            {/* Credits Badge (desktop) — leads to top-ups. Free accounts go to
                plans instead, since top-ups are gated behind a paid plan. */}
            {credits && (
              <button
                type="button"
                onClick={() => navigate(isFreePlan ? '/plans' : '/topups')}
                title={isFreePlan ? 'Upgrade your plan' : 'Buy more credits'}
                className="group hidden lg:flex items-center gap-2.5 px-3.5 py-2 rounded-xl bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.08] hover:border-cyan-400/40 dark:hover:border-cyan-400/30 hover:shadow-[0_0_12px_rgba(34,211,238,0.12)] transition-all duration-200 cursor-pointer"
              >
                <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  credits.available / credits.total < 0.2
                    ? 'bg-amber-400 animate-pulse'
                    : 'bg-emerald-400'
                }`} />
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300 tabular-nums">
                  {formatCredits(credits.available)}
                </span>
                <span className="text-xs text-gray-500">/</span>
                <span className="text-xs text-gray-500 tabular-nums">
                  {formatCredits(credits.total)}
                </span>
                <div className="w-14 h-1.5 rounded-full bg-gray-200 dark:bg-gray-800/80 overflow-hidden ring-1 ring-inset ring-gray-300/50 dark:ring-white/[0.04]">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 transition-all duration-500 shadow-[0_0_6px_rgba(34,211,238,0.4)]"
                    style={{ width: credits.total > 0 ? `${((credits.available / credits.total) * 100).toFixed(1)}%` : '0%' }}
                  />
                </div>
                {!isFreePlan && (
                  <HiOutlinePlusCircle className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 group-hover:text-cyan-500 dark:group-hover:text-cyan-400 transition-colors" />
                )}
              </button>
            )}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-full text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <HiOutlineSun className="w-[18px] h-[18px]" /> : <HiOutlineMoon className="w-[18px] h-[18px]" />}
            </button>

            {/* User Avatar / Menu Trigger */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
              >
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name || 'User'}
                    referrerPolicy="no-referrer"
                    className="w-7 h-7 rounded-full object-cover ring-2 ring-gray-200 dark:ring-white/[0.1]"
                  />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 via-cyan-400 to-purple-400 flex items-center justify-center ring-2 ring-gray-200 dark:ring-white/[0.1] shadow-[0_0_10px_rgba(34,211,238,0.15)]">
                    <span className="text-[10px] font-bold text-white">
                      {getInitials(user?.name)}
                    </span>
                  </div>
                )}
                <HiOutlineChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 hidden sm:block ${userMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* User Dropdown Menu */}
              {userMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 z-50 w-72 max-w-[calc(100vw-2rem)] bg-white dark:bg-gray-900 border border-gray-200 dark:border-white/[0.08] rounded-2xl shadow-2xl shadow-black/10 dark:shadow-black/40 overflow-hidden animate-fade-in ring-1 ring-inset ring-gray-100 dark:ring-white/[0.04]">

                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-white/[0.08]">
                      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                        {user?.name || 'User'}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {user?.email}
                      </p>
                    </div>

                    {/* Workspace Selector */}
                    {sortedWorkspaces.length > 0 && (
                      <div className="px-2 py-2 border-b border-gray-200 dark:border-white/[0.08]">
                        <p className="px-2 pb-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                          Workspace
                        </p>
                        {sortedWorkspaces.map((workspace) => {
                          const isOwnerRow = workspace.role === 'owner';
                          return (
                            <button
                              key={workspace.id}
                              onClick={() => handleWorkspaceSwitch(workspace.id)}
                              className={`w-full px-2 py-2 rounded-lg text-left flex items-center gap-2.5 transition-colors ${
                                workspace.id === currentWorkspace?.id
                                  ? 'bg-gray-100 dark:bg-white/10'
                                  : 'hover:bg-gray-50 dark:hover:bg-white/[0.06]'
                              }`}
                            >
                              <div className="w-5 h-5 rounded-md bg-gradient-to-br from-blue-400/20 to-cyan-500/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-[9px] font-bold text-blue-400">
                                  {workspace.name?.charAt(0)?.toUpperCase()}
                                </span>
                              </div>
                              <span className="text-sm text-gray-700 dark:text-gray-200 font-medium flex-1 truncate">
                                {workspace.name}
                              </span>
                              <span
                                className={`text-[9px] px-1.5 py-0.5 rounded-md font-semibold uppercase tracking-wide flex-shrink-0 ${
                                  isOwnerRow
                                    ? 'bg-blue-400/15 text-blue-600 dark:text-blue-400'
                                    : 'bg-gray-400/15 text-gray-500 dark:text-gray-400'
                                }`}
                              >
                                {isOwnerRow ? 'Owner' : 'Member'}
                              </span>
                              {workspace.id === currentWorkspace?.id && (
                                <HiOutlineCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Credits (mobile - hidden on desktop since badge shows) */}
                    <button
                      type="button"
                      onClick={() => { navigate(isFreePlan ? '/plans' : '/topups'); setUserMenuOpen(false); }}
                      className="block lg:hidden w-full text-left px-4 py-3 border-b border-gray-200 dark:border-white/[0.08] hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-colors"
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-gray-500 font-medium">Credits</span>
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 tabular-nums">
                          {credits ? `${formatCredits(credits.available)} / ${formatCredits(credits.total)}` : '--'}
                        </span>
                      </div>
                      <div className="w-full h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 transition-all duration-500"
                          style={{ width: credits?.total > 0 ? `${((credits.available / credits.total) * 100).toFixed(1)}%` : '0%' }}
                        />
                      </div>
                    </button>

                    {/* Menu Items */}
                    <div className="px-2 py-2">
                      <button
                        onClick={() => { navigate('/dashboard/account'); setUserMenuOpen(false); }}
                        className="w-full px-2 py-2 rounded-lg text-left flex items-center gap-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.06] transition-colors"
                      >
                        <HiOutlineUser className="w-4 h-4" />
                        <span className="text-sm font-medium">Account</span>
                      </button>
                      <button
                        onClick={() => { setSupportModalOpen(true); setUserMenuOpen(false); }}
                        className="w-full px-2 py-2 rounded-lg text-left flex items-center gap-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.06] transition-colors"
                      >
                        <HiOutlineLifebuoy className="w-4 h-4" />
                        <span className="text-sm font-medium">Support</span>
                        {supportUnreadCount > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none ml-auto">
                            {supportUnreadCount > 9 ? '9+' : supportUnreadCount}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => { navigate('/plans'); setUserMenuOpen(false); }}
                        className="w-full px-2 py-2 rounded-lg text-left flex items-center gap-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.06] transition-colors"
                      >
                        <HiOutlineCreditCard className="w-4 h-4" />
                        <span className="text-sm font-medium">Plans</span>
                      </button>
                      {!isFreePlan && planName && (
                        <button
                          onClick={() => { navigate('/topups'); setUserMenuOpen(false); }}
                          className="w-full px-2 py-2 rounded-lg text-left flex items-center gap-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.06] transition-colors"
                        >
                          <HiOutlinePlusCircle className="w-4 h-4 text-cyan-500 dark:text-cyan-400" />
                          <span className="text-sm font-medium">Buy Credits</span>
                        </button>
                      )}
                      {isAdmin && (
                        <button
                          onClick={() => { navigate('/dashboard/admin'); setUserMenuOpen(false); }}
                          className="w-full px-2 py-2 rounded-lg text-left flex items-center gap-2.5 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/[0.06] transition-colors"
                        >
                          <HiOutlineCog6Tooth className="w-4 h-4" />
                          <span className="text-sm font-medium">Admin</span>
                        </button>
                      )}
                    </div>

                    {/* Sign Out */}
                    <div className="px-2 py-2 border-t border-gray-200 dark:border-white/[0.08]">
                      <button
                        onClick={async () => { await logout(); navigate('/signin'); }}
                        className="w-full px-2 py-2 rounded-lg text-left flex items-center gap-2.5 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                      >
                        <HiOutlineArrowRightOnRectangle className="w-4 h-4" />
                        <span className="text-sm font-medium">Sign Out</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        {/* Mobile Navigation Sheet */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 mx-3 mt-2 z-40 lg:hidden bg-white/95 dark:bg-gray-950/95 backdrop-blur-xl rounded-2xl ring-1 ring-gray-200 dark:ring-white/[0.06] shadow-lg dark:shadow-xl animate-slide-in-from-top">
            <nav className="px-4 py-3 space-y-1">
              {mainNavItems.filter(item => !item.hover).map((item) => {
                const Icon = item.icon;
                const isActive = activeView === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      item.onClick ? item.onClick() : navigate(`/dashboard/${item.id}`);
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-gray-100 dark:bg-white/10 text-gray-900 dark:text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06]'
                    }`}
                  >
                    <Icon className="w-[18px] h-[18px]" />
                    <span>{item.label}</span>
                    {item.badge > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full leading-none ml-auto">
                        {item.badge > 9 ? '9+' : item.badge}
                      </span>
                    )}
                  </button>
                );
              })}

              <TutorialsMobileList onItemClick={() => setMobileMenuOpen(false)} />

              {isFreePlan && activeView !== 'plans' && (
                <button
                  onClick={() => { navigate('/plans'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white shadow-md shadow-cyan-500/25 transition-all"
                >
                  <HiOutlineCreditCard className="w-[18px] h-[18px] text-white/90" />
                  <span className="uppercase tracking-wide">Upgrade Plan</span>
                </button>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20 dark:bg-black/40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Status Announcements — page-level, above the content card */}
      {!isPaywalled && announcements.length > 0 && (
        <div className="flex-shrink-0 px-3 lg:px-4 pt-2 lg:pt-3 flex justify-center">
          <div className="max-w-[1400px] w-full space-y-2">
            {announcements.map((a) => (
              <AnnouncementBanner key={a.id} announcement={a} onDismiss={dismissAnnouncement} />
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-hidden px-3 lg:px-4 py-3 lg:py-3 flex justify-center">
        <div className="h-full bg-white dark:bg-gray-950 rounded-2xl overflow-hidden ring-1 ring-gray-200/60 dark:ring-white/[0.06] shadow-sm dark:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.03)] max-w-[1400px] w-full">
          <div className="h-full overflow-y-auto custom-scrollbar px-3 lg:px-6 py-3 lg:py-5">
            {renderView()}
          </div>
        </div>
      </main>

      {/* Support Modal */}
      <SupportModal
        isOpen={supportModalOpen}
        onClose={() => { setSupportModalOpen(false); setSupportInitialTicketId(null); setSupportPrefill(null); }}
        initialTab={supportInitialTicketId ? 'tickets' : supportPrefill ? 'new' : (supportUnreadCount > 0 ? 'tickets' : 'new')}
        initialTicketId={supportInitialTicketId}
        prefill={supportPrefill}
      />

      <style dangerouslySetInnerHTML={{
        __html: `
          .custom-scrollbar::-webkit-scrollbar {
            width: 6px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(75, 85, 99, 0.4);
            border-radius: 3px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(75, 85, 99, 0.6);
          }
        `,
      }} />
    </div>
    </SupportProvider>
  );
};

export default DashboardView;
